import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Section from '../../Components/Section/Section';

// Components
import StoreHeader from './components/StoreHeader';
import TotalOrdersCard from './components/TotalOrdersCard';
import InventorySection from './components/InventorySection';
import OrdersToHandle from './components/OrdersToHandle';
import IntegratedOrdersCard from './components/IntegratedOrdersCard';
import QuickActions from './components/QuickActions';
import SalesPerformance from './components/SalesPerformance';

// Custom Hook
import { useStoreDashboard } from './hooks/useStoreDashboard';

const StoreDashboard: React.FC = () => {
  const {
    storeName,
    storeLogo,
    timeFilter,
    isStoreOpen,
    openingTime,
    closingTime,
    summary,
    summaryLoading,
    ordersLoading,
    activeOrderTab,
    updatingOrderId,
    tabCounts,
    displayedOrders,
    statsLoading,
    deliveryPercentage,
    setActiveOrderTab,
    handlePeriodChange,
    handleUpdateOrderStatus,
    handleNavigateOutOfStock,
    handleNavigateLowStock,
    handleViewAllOrders,
    handleViewAllIntegratedOrders,
    handleAddProduct,
    handleViewProducts,
    handleCreateOffer,
    handleEditStorePage,
    refreshOrders,
  } = useStoreDashboard();

  return (
    <Section
      className="storeDashboard"
      style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}
    >
      <Container className="py-4">
        {/* 1. Store Header & Controls */}
        <StoreHeader
          storeName={storeName}
          storeLogo={storeLogo}
          timeFilter={timeFilter}
          isStoreOpen={isStoreOpen}
          openingTime={openingTime}
          closingTime={closingTime}
          onPeriodChange={handlePeriodChange}
        />

        {/* 2. Top Row: Total Orders & Inventory */}
        <Row className="g-4 mb-4">
          <Col lg={6}>
            <TotalOrdersCard
              summary={summary}
              summaryLoading={summaryLoading}
              onViewAll={handleViewAllOrders}
            />
          </Col>
          <Col lg={6}>
            <InventorySection
              outOfStockCount={
                summary?.total_products_with_breakdown?.out_of_stock ?? 0
              }
              lowStockCount={
                summary?.total_products_with_breakdown?.low_stock ?? 0
              }
              inventoryLoading={summaryLoading}
              onNavigateOutOfStock={handleNavigateOutOfStock}
              onNavigateLowStock={handleNavigateLowStock}
            />
          </Col>
        </Row>

        {/* 3. Middle Row: Orders to handle & Right Column */}
        <Row className="g-4 mb-4">
          <Col lg={7}>
            <OrdersToHandle
              orders={displayedOrders}
              ordersLoading={ordersLoading}
              activeOrderTab={activeOrderTab}
              tabCounts={tabCounts}
              updatingOrderId={updatingOrderId}
              onTabChange={setActiveOrderTab}
              onUpdateStatus={handleUpdateOrderStatus}
              onOrdersRefresh={refreshOrders}
              onViewAllOrders={handleViewAllOrders}
            />
          </Col>
          <Col lg={5}>
            <div className="d-flex flex-column gap-4 h-100">
              <IntegratedOrdersCard
                integratedOrdersBreakdown={
                  summary?.total_integrated_order_with_breakdown || {
                    total: 0,
                    pending: 0,
                    synced: 0,
                    failed: 0,
                    in_transit: 0,
                    out_for_delivery: 0,
                    delivered: 0,
                    cancelled: 0,
                    returned: 0,
                  }
                }
                statsLoading={statsLoading}
                deliveryPercentage={deliveryPercentage}
                onViewAll={handleViewAllIntegratedOrders}
              />
              <QuickActions
                onAddProduct={handleAddProduct}
                onViewProducts={handleViewProducts}
                onCreateOffer={handleCreateOffer}
                onEditStorePage={handleEditStorePage}
              />
            </div>
          </Col>
        </Row>

        {/* 4. Bottom Section: Sales Performance */}
        <SalesPerformance
          storeRevenue={summary?.store_revenue.total_amount || '0.00'}
          revenueLoading={summaryLoading}
          fulfilledOrders={summary?.store_revenue.total_orders || 0}
          averageOrderValue={
            summary?.store_revenue.average_order_value || '0.00'
          }
          summaryLoading={summaryLoading}
          dailyRevenue={summary?.store_revenue.daily_data || []}
        />
      </Container>
    </Section>
  );
};

export default StoreDashboard;
